import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatBRL, formatDate, apiErrorMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Receipt, Upload, Check, Clock, DollarSign, Download, AlertCircle, Ban } from "lucide-react";
import { toast } from "sonner";
import { FiscalFiltros, FILTROS_VAZIOS } from "@/components/fiscal/FiscalFiltros";
import { NfStatusCell, NfAcoesMenu } from "@/components/fiscal/NfLinha";
import { CancelarNfModal } from "@/components/fiscal/CancelarNfModal";
import { HistoricoNfModal } from "@/components/fiscal/HistoricoNfModal";
import { EmitirNfModal } from "@/components/fiscal/EmitirNfModal";
import { NfsACancelar } from "@/components/fiscal/NfsACancelar";
import { datasVenda } from "@/components/fiscal/NfLinha";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PARAMS = [["ano", "ano"], ["mes", "mes"], ["mesCurso", "mes_curso"], ["uf", "uf"], ["ufCurso", "uf_curso"], ["curso", "curso_id"]];
const EVENTOS_RELOAD = ["nf_pendente", "nf_emitida", "nf_revisao_solicitada", "nf_cancelada", "nf_cancelamento_pedido"];
const stDe = (i) => i.nf_status || "pendente";

function KPI({ label, value, icon: Ico, cor, testid, onClick, active }) {
  return (
    <Card
      className={`p-4 rounded-2xl transition-shadow ${onClick ? "cursor-pointer" : ""} ${active ? "ring-2 ring-offset-2 ring-red-500 shadow-md" : "hover:shadow"}`}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cor}`}><Ico className="w-5 h-5" /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
          <div className="font-display font-bold text-xl text-slate-900 tabular-nums truncate">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function Kpis({ k, f, setF }) {
  const status = (s) => () => setF((x) => ({ ...x, status: s }));
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <KPI label="Pendentes" value={k.pendentes || 0} icon={AlertCircle} cor="bg-amber-100 text-amber-700" testid="kpi-pendentes" onClick={status("pendente")} />
      <KPI label="Emitidas" value={k.emitidas || 0} icon={Check} cor="bg-emerald-100 text-emerald-700" testid="kpi-emitidas" onClick={status("emitida")} />
      <KPI label="Precisam revisão" value={k.precisam_revisao || 0} icon={AlertCircle} cor="bg-red-100 text-red-700" testid="kpi-revisao"
        active={f.revisao} onClick={() => setF((x) => ({ ...x, revisao: !x.revisao }))} />
      <KPI label="Canceladas" value={k.canceladas || 0} icon={Ban} cor="bg-slate-200 text-slate-700" testid="kpi-canceladas" onClick={status("cancelada")} />
      <KPI label="Valor pendente" value={formatBRL(k.valor_pendente || 0)} icon={Clock} cor="bg-red-100 text-red-700" testid="kpi-valor-pendente" />
      <KPI label="Valor emitido" value={formatBRL(k.valor_emitido || 0)} icon={DollarSign} cor="bg-blue-100 text-blue-700" testid="kpi-valor-emitido" />
    </div>
  );
}

function BarraSelecao({ sel, total, onCancelar, onLimpar }) {
  if (!sel.length) return null;
  return (
    <Card className="p-3 rounded-2xl flex items-center gap-3 flex-wrap border-red-200 bg-red-50" data-testid="fiscal-barra-selecao">
      <span className="text-sm font-bold text-red-900">{sel.length} solicitaç{sel.length === 1 ? "ão" : "ões"} selecionada{sel.length === 1 ? "" : "s"} · {formatBRL(total)}</span>
      <Button size="sm" onClick={onCancelar} className="bg-red-600 hover:bg-red-700" data-testid="btn-cancelar-selecionadas"><Ban className="w-3.5 h-3.5 mr-1" /> Cancelar selecionadas</Button>
      <button onClick={onLimpar} className="text-xs font-bold text-slate-500 hover:text-slate-800" data-testid="btn-limpar-selecao">Limpar seleção</button>
    </Card>
  );
}

function BotaoEmitir({ i, onEmitir }) {
  const st = stDe(i);
  if (st === "cancelada") return null;
  const alerta = i.nf_precisa_revisao || i.nf_substituir?.length > 0 || (st === "emitida" && i.alterada_apos_emissao);
  const cls = alerta ? "bg-red-500 hover:bg-red-600 text-white" : st === "pendente" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300";
  const rotulo = i.nf_substituir?.length > 0 ? "Emitir nova" : st === "pendente" ? "Emitir" : alerta ? "Reemitir" : "Reenviar";
  return (
    <Button size="sm" onClick={() => onEmitir(i)} className={`text-xs ${cls}`} data-testid={`btn-upload-${i.id}`}>
      <Upload className="w-3 h-3 mr-1" /> {rotulo}
    </Button>
  );
}

function LinhaNf({ i, marcado, onToggle, acoes }) {
  const st = stDe(i);
  const alerta = st !== "cancelada" && (i.nf_precisa_revisao || i.nf_substituir?.length > 0 || (st === "emitida" && i.alterada_apos_emissao));
  const fundo = st === "cancelada" ? "bg-slate-50 text-slate-500" : alerta ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50";
  const parar = (e) => e.stopPropagation();
  return (
    <tr className={`border-t cursor-pointer ${fundo}`} onClick={() => acoes.onHistorico(i)} data-testid={`fiscal-row-${i.id}`}>
      <td className="p-2 w-8" onClick={parar}>
        {st === "pendente" && <input type="checkbox" checked={marcado} onChange={() => onToggle(i.id)} className="w-4 h-4 accent-red-600" data-testid={`sel-${i.id}`} />}
      </td>
      <td className="p-2 whitespace-nowrap" data-testid={`fiscal-data-${i.id}`}>
        {datasVenda(i).map((d, k) => <div key={k} className={k ? "text-[10px] text-slate-500" : ""}>{k ? "+ " : ""}{formatDate(d)}</div>)}
      </td>
      <td className="p-2 whitespace-nowrap">{i.vendedora_nome}</td>
      <td className="p-2 whitespace-nowrap">
        {i.municipio_nome || <span className="text-slate-400">—</span>}
        {i.municipio_uf ? <span className="text-xs text-slate-400 ml-1">/ {i.municipio_uf}</span> : null}
      </td>
      <td className="p-2 truncate max-w-[260px]" title={i.curso_rotulo || i.curso_nome} data-testid={`fiscal-curso-${i.id}`}>{i.curso_rotulo || i.curso_nome}</td>
      <td className="p-2 text-right tabular-nums">
        {i.quantidade}
        {i.cortesias > 0 && <div className="text-[10px] text-sky-700 whitespace-nowrap" data-testid={`fiscal-cortesias-${i.id}`}>{i.cortesias} cortesia</div>}
      </td>
      <td className={`p-2 text-right tabular-nums font-semibold ${st === "cancelada" ? "line-through" : ""}`}>{formatBRL(i.valor_vendido)}</td>
      <td className="p-2"><NfStatusCell i={i} /></td>
      <td className="p-2 text-slate-600">
        {i.nf_numero || <span className="text-slate-400">—</span>}
        {i.nf_uploaded_at && <div className="text-[10px] text-slate-400">{new Date(i.nf_uploaded_at).toLocaleString("pt-BR")}</div>}
      </td>
      <td className="p-2 text-right whitespace-nowrap" onClick={parar}>
        {st === "emitida" && (
          <Button size="sm" variant="outline" onClick={() => acoes.onDownload(i)} className="text-xs mr-1" data-testid={`btn-download-${i.id}`}><Download className="w-3 h-3" /></Button>
        )}
        <BotaoEmitir i={i} onEmitir={acoes.onEmitir} />
        <NfAcoesMenu i={i} onHistorico={acoes.onHistorico} onCancelar={acoes.onCancelar} onDesfazer={acoes.onDesfazer} />
      </td>
    </tr>
  );
}

function TabelaNf({ inscricoes, sel, setSel, acoes }) {
  const pend = inscricoes.filter((i) => stDe(i) === "pendente").map((i) => i.id);
  const todas = pend.length > 0 && pend.every((id) => sel.includes(id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <Card className="p-0 rounded-2xl overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
              <th className="p-2 w-8">
                {pend.length > 0 && <input type="checkbox" checked={todas} onChange={() => setSel(todas ? [] : pend)} className="w-4 h-4 accent-red-600" title="Selecionar pendentes" data-testid="sel-todas" />}
              </th>
              <th className="p-2">Data venda</th><th className="p-2">Vendedora</th><th className="p-2">Município</th>
              <th className="p-2">Curso</th><th className="p-2 text-right">Qtd</th><th className="p-2 text-right">Valor</th>
              <th className="p-2">Status NF</th><th className="p-2">NF Nº</th><th className="p-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {inscricoes.map((i) => <LinhaNf key={i.id} i={i} marcado={sel.includes(i.id)} onToggle={toggle} acoes={acoes} />)}
            {inscricoes.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-slate-400" data-testid="fiscal-vazio">Nenhuma inscrição encontrada.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Fiscal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { lastEvent } = useRealtime() || {};
  const [data, setData] = useState({ inscricoes: [], kpis: {}, cursos: [], ufs_cliente: [], ufs_curso: [] });
  const [f, setF] = useState(FILTROS_VAZIOS);
  const [sel, setSel] = useState([]);
  const [emitirIns, setEmitirIns] = useState(null);
  const [cancelar, setCancelar] = useState(null);
  const [histIns, setHistIns] = useState(null);
  const [desfazerIns, setDesfazerIns] = useState(null);

  useEffect(() => {
    if (user && user.role !== "fiscal" && user.role !== "diretoria") {
      nav(user.role === "vendedora" || user.role === "supervisao" ? "/minhas-nfs" : "/funil");
    }
  }, [user, nav]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (f.status !== "todos") p.set("status", f.status);
    PARAMS.forEach(([k, q]) => f[k] && p.set(q, f[k]));
    if (f.revisao) p.set("precisa_revisao", "true");
    api.get(`/fiscal/inscricoes?${p.toString()}`).then((r) => { setData(r.data); setSel([]); }).catch(() => {});
  }, [f]);
  useEffect(() => { load(); }, [load]);

  // Filtro Curso em cascata: só cursos compatíveis com Ano, Mês do curso e Local do curso
  const cursosCompat = useMemo(() => (data.cursos || []).filter((c) =>
    (!f.ano || !f.mesCurso || (c.mes || "").startsWith(String(f.ano)))
    && (!f.mesCurso || (c.mes || "").slice(5, 7) === String(f.mesCurso).padStart(2, "0"))
    && (!f.ufCurso || (c.uf || "").toUpperCase() === f.ufCurso.toUpperCase())), [data.cursos, f.ano, f.mesCurso, f.ufCurso]);
  useEffect(() => {
    if (f.curso && data.cursos?.length && !cursosCompat.some((c) => c.id === f.curso)) setF((x) => ({ ...x, curso: "" }));
  }, [cursosCompat, f.curso, data.cursos]);

  useEffect(() => {
    if (!lastEvent) return;
    const ev = lastEvent.event;
    if (ev === "nf_pendente") toast.info(`📄 Nova NF pendente: ${lastEvent.vendedora_nome} · ${lastEvent.municipio_nome} · ${formatBRL(lastEvent.valor)}`);
    if (ev === "nf_revisao_solicitada") {
      toast.warning(`🚨 Revisão de NF pedida: ${lastEvent.solicitante_nome} · ${lastEvent.municipio_nome}`, { description: lastEvent.observacao, duration: 10000 });
    }
    if (ev === "nf_cancelamento_pedido") toast.warning(`Cancelamento de NF pedido: ${lastEvent.solicitante_nome} · ${lastEvent.municipio_nome}`);
    if (ev === "nf_a_cancelar") toast.error(`Inscrição cancelada: cancele a NF nº ${(lastEvent.nf_numeros || []).join(", ")} · ${lastEvent.municipio_nome}`, { duration: 10000 });
    if (EVENTOS_RELOAD.includes(ev)) load();
  }, [lastEvent, load]);

  const downloadNF = async (ins) => {
    try {
      const r = await api.get(`/fiscal/inscricoes/${ins.id}/nf/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = ins.nf_arquivo_nome || `NF_${ins.nf_numero || ins.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar"); }
  };

  const desfazer = async () => {
    const ins = desfazerIns;
    setDesfazerIns(null);
    try {
      await api.post(`/fiscal/inscricoes/${ins.id}/desfazer-cancelamento`);
      toast.success("Cancelamento desfeito · voltou para Pendente");
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro ao desfazer")); }
  };

  const acoes = {
    onHistorico: setHistIns, onDownload: downloadNF, onEmitir: setEmitirIns, onDesfazer: setDesfazerIns,
    onCancelar: (ins) => setCancelar({ ins }),
  };
  const totalSel = data.inscricoes.filter((i) => sel.includes(i.id)).reduce((s, i) => s + Number(i.valor_vendido || 0), 0);
  const fecharEAtualizar = (fechar) => () => { fechar(null); load(); };

  return (
    <div className="space-y-6" data-testid="fiscal-page">
      <div className="flex items-center gap-2">
        <Receipt className="w-6 h-6 text-amber-600" />
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Fiscal · Notas Fiscais</h1>
      </div>
      <Kpis k={data.kpis || {}} f={f} setF={setF} />
      <FiscalFiltros f={f} setF={setF} cursos={cursosCompat} todosCursos={data.cursos || []} ufsCliente={data.ufs_cliente || []} ufsCurso={data.ufs_curso || []} />
      <NfsACancelar />
      <BarraSelecao sel={sel} total={totalSel} onCancelar={() => setCancelar({ ids: sel })} onLimpar={() => setSel([])} />
      <TabelaNf inscricoes={data.inscricoes} sel={sel} setSel={setSel} acoes={acoes} />
      <EmitirNfModal ins={emitirIns} onClose={() => setEmitirIns(null)} onSaved={fecharEAtualizar(setEmitirIns)} />
      <CancelarNfModal ins={cancelar?.ins} ids={cancelar?.ids} onClose={() => setCancelar(null)} onDone={fecharEAtualizar(setCancelar)} />
      <HistoricoNfModal ins={histIns} onClose={() => setHistIns(null)} />
      <AlertDialog open={!!desfazerIns} onOpenChange={(v) => !v && setDesfazerIns(null)}>
        <AlertDialogContent data-testid="modal-desfazer-cancel">
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer o cancelamento?</AlertDialogTitle>
            <AlertDialogDescription>A solicitação de {desfazerIns?.municipio_nome} volta para Pendente e entra de novo nos totais.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="desfazer-voltar">Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={desfazer} data-testid="desfazer-confirmar">Desfazer cancelamento</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
